import { supabase } from '../supabaseClient';
import { GoogleGenerativeAI } from '@google/generative-ai';

const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY || '');

const normalizeText = (text) => {
  if (!text) return '';
  return text.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
};

const getProxiedImageUrl = (url) => {
  if (!url) return null;
  const RENDER_API = process.env.REACT_APP_RENDER_API || 'https://amorlia-scraper-production.up.railway.app';
  return `${RENDER_API}/proxy-image?url=${encodeURIComponent(url)}`;
};

async function fetchImageForGemini(url) {
  try {
    const proxiedUrl = getProxiedImageUrl(url);
    if (!proxiedUrl) return null;
    const response = await fetch(proxiedUrl);
    if (!response.ok) throw new Error('Failed to fetch image');
    const blob = await response.blob();
    const reader = new FileReader();
    return new Promise((resolve) => {
      reader.onloadend = () => {
        const base64Data = reader.result.split(',')[1];
        resolve({ inlineData: { data: base64Data, mimeType: blob.type || 'image/jpeg' } });
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error preparing image for Gemini:', error);
    return null;
  }
}

let cachedRules = null;
let lastFetch = 0;

async function getRules() {
  if (cachedRules && Date.now() - lastFetch < 1000 * 60 * 5) return cachedRules;
  try {
    const [bannedRes, materialRes, styleRes] = await Promise.all([
      supabase.from('banned_keywords').select('*'),
      supabase.from('material_mappings').select('*'),
      supabase.from('style_mappings').select('*')
    ]);
    cachedRules = {
      banned: bannedRes.data || [],
      materials: materialRes.data || [],
      styles: styleRes.data || []
    };
    lastFetch = Date.now();
    return cachedRules;
  } catch (error) {
    console.error('Error fetching Supabase rules:', error);
    return { banned: [], materials: [], styles: [] };
  }
}

export async function optimizeProductData(product) {
  try {
    const rules = await getRules();

    const rawTitle = (product._rawTitle || product.title || product.variantName || '').trim();
    const rawDesc = (product.description || '').trim();
    const fullTextContext = normalizeText(`${rawTitle} ${rawDesc}`);

    let itemSpecifics = {
      Brand: 'Unbranded',
      Type: 'Jewelry',
      Department: 'Women',
      Color: 'Multi-Color',
      Material: 'Unknown',
      Style: 'Unknown',
    };

    if (product.itemSpecifics) {
      const pi = product.itemSpecifics;
      // Seed all detected values from slPipeline so Gemini only fires for truly unknown fields
      if (pi.Brand) itemSpecifics.Brand = pi.Brand;
      if (pi.Department) itemSpecifics.Department = pi.Department;
      if (pi.Type && pi.Type !== 'Jewelry') itemSpecifics.Type = pi.Type;
      if (pi.Material && pi.Material !== 'Unknown') itemSpecifics.Material = pi.Material;
      if (pi.Style && pi.Style !== 'Unknown' && pi.Style !== 'Classic') itemSpecifics.Style = pi.Style;
      if (pi.Color && pi.Color !== 'Multi-Color') itemSpecifics.Color = pi.Color;
    }

    const matchedMaterial = rules.materials.find(m => fullTextContext.includes(normalizeText(m.source_keyword)));
    if (matchedMaterial) itemSpecifics.Material = matchedMaterial.ebay_specific_value;

    const matchedStyle = rules.styles.find(s => fullTextContext.includes(normalizeText(s.source_keyword)));
    if (matchedStyle) itemSpecifics.Style = matchedStyle.ebay_specific_value;

    if (fullTextContext.includes('rose gold')) itemSpecifics.Color = 'Rose Gold';
    else if (fullTextContext.includes('gold')) itemSpecifics.Color = 'Gold';
    else if (fullTextContext.includes('silver')) itemSpecifics.Color = 'Silver';
    else if (fullTextContext.includes('black')) itemSpecifics.Color = 'Black';

    if (fullTextContext.includes('necklace') || fullTextContext.includes('pendant')) itemSpecifics.Type = 'Necklace';
    else if (fullTextContext.includes('ring')) itemSpecifics.Type = 'Ring';
    else if (fullTextContext.includes('earring')) itemSpecifics.Type = 'Earrings';
    else if (fullTextContext.includes('bracelet') || fullTextContext.includes('bangle')) itemSpecifics.Type = 'Bracelet';
    else if (fullTextContext.includes('set')) itemSpecifics.Type = 'Jewelry Set';

    let usedGemini = false;
    let geminiLog = '';

    if ((itemSpecifics.Material === 'Unknown' || itemSpecifics.Style === 'Unknown' || itemSpecifics.Type === 'Jewelry') && product.image && process.env.REACT_APP_GEMINI_API_KEY) {
      const imageData = await fetchImageForGemini(product.image);
      if (imageData) {
        try {
          const model = genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });
          const prompt = `Analyze this jewelry image. Title: "${rawTitle}". Return pure JSON only, no markdown: {"color":"Gold/Silver/Rose Gold/Multi-Color","type":"Necklace/Ring/Earrings/Bracelet/Jewelry Set","style":"brief style description","material_guess":"metal type guess"}`;
          const result = await model.generateContent([prompt, imageData]);
          const responseText = result.response.text();
          const jsonMatch = responseText.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const aiData = JSON.parse(jsonMatch[0]);
            usedGemini = true;
            geminiLog = `Gemini inferred: ${JSON.stringify(aiData)}`;
            if (itemSpecifics.Color === 'Multi-Color' && aiData.color) itemSpecifics.Color = aiData.color;
            if (itemSpecifics.Type === 'Jewelry' && aiData.type) itemSpecifics.Type = aiData.type;
            if (itemSpecifics.Style === 'Unknown' && aiData.style) itemSpecifics.Style = aiData.style;
            if (itemSpecifics.Material === 'Unknown' && aiData.material_guess) itemSpecifics.Material = aiData.material_guess;
          }
        } catch (geminiError) {
          geminiLog = 'Gemini fallback failed: ' + geminiError.message;
        }
      }
    }

    const baseTitle = rawTitle.replace(/nihao|nihaojewelry|nhdropshipping/gi, '').replace(/\s+/g, ' ').trim();

    let optimizedTitle = baseTitle.length >= 5
      ? baseTitle.slice(0, 80)
      : [
        itemSpecifics.Color !== 'Multi-Color' ? itemSpecifics.Color : '',
        itemSpecifics.Type !== 'Jewelry' ? itemSpecifics.Type : '',
        itemSpecifics.Material !== 'Unknown' ? itemSpecifics.Material : ''
      ].filter(Boolean).join(' ').slice(0, 80) || 'Jewelry';

    let cleanDesc = rawDesc.length >= 20 ? rawDesc : '';
    if (!cleanDesc) {
      cleanDesc = `${optimizedTitle}. Material: ${itemSpecifics.Material !== 'Unknown' ? itemSpecifics.Material : 'Stainless Steel'}. Style: ${itemSpecifics.Style !== 'Unknown' ? itemSpecifics.Style : 'Classic'}. Tarnish resistant. Perfect for everyday wear and gifting.`;
    }

    const productType = itemSpecifics.Type !== 'Jewelry' ? itemSpecifics.Type.toLowerCase() : 'jewelry';
    const productMaterial = itemSpecifics.Material !== 'Unknown' ? itemSpecifics.Material.toLowerCase() : '';
    const productStyle = itemSpecifics.Style !== 'Unknown' ? itemSpecifics.Style.toLowerCase() : '';
    const productColor = itemSpecifics.Color !== 'Multi-Color' ? itemSpecifics.Color.toLowerCase() : '';

    const tagPool = [
      productColor ? `${productColor} ${productType}` : null,
      productStyle ? `${productStyle} ${productType}` : null,
      productMaterial ? `${productMaterial} ${productType}` : null,
      'minimalist jewelry',
      'gift for her',
      'tarnish free',
      `dainty ${productType}`,
      `everyday ${productType}`,
      `trendy ${productType}`,
      'statement piece',
      'boho jewelry',
      'layered look',
      'waterproof jewelry',
      'tarnish resistant',
    ].filter(Boolean);

    const tags = [...new Set(tagPool)].slice(0, 13);

    return {
      success: true,
      data: {
        title: optimizedTitle,
        description: cleanDesc,
        itemSpecifics,
        tags,
        _intelligenceLog: { usedGemini, geminiLog, rulesApplied: true }
      }
    };

  } catch (error) {
    console.error('Intelligence Engine Error:', error);
    return { success: false, error: error.message };
  }
}